import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../../components/Button.jsx';

const ADMIN_BLUE = '#6FB1F7';

const formatLastLogin = (iso) => {
  if (!iso) return 'Never';
  const dt = new Date(iso);
  const diff = (Date.now() - dt.getTime()) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return dt.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};

const RolePill = ({ role }) => (
  <span
    class={`inline-flex items-center rounded-full border px-2 py-[2px] text-[10px] font-semibold uppercase tracking-[0.04em] ${
      role === 'admin'
        ? 'border-status-info/30 bg-status-info/12 text-status-info'
        : 'border-border-strong text-text-tertiary'
    }`}
  >
    {role}
  </span>
);

const UserModal = ({ open, targetUser, currentUserId, onClose, onSaved }) => {
  const editing = !!targetUser;
  const isSelf = editing && targetUser.id === currentUserId;
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [resettingPin, setResettingPin] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const [pinMessage, setPinMessage] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(editing
        ? { username: targetUser.username, displayName: targetUser.displayName, role: targetUser.role, pin: '' }
        : { username: '', displayName: '', role: 'user', pin: '' });
      setError(null);
      setResettingPin(false);
      setNewPin('');
      setPinMessage(null);
    }
  }, [open, editing, targetUser]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.currentTarget.value }));

  const save = async () => {
    if (!form.displayName?.trim() || (!editing && (!form.username?.trim() || !form.pin?.trim()))) return;
    setBusy(true); setError(null);
    try {
      const saved = editing
        ? await api.patch(`/users/${targetUser.id}`, { displayName: form.displayName.trim(), role: form.role })
        : await api.post('/users', {
            username: form.username.trim(),
            displayName: form.displayName.trim(),
            role: form.role,
            pin: form.pin.trim(),
          });
      onSaved?.(saved);
    } catch (err) { setError(err); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm(`Delete "${targetUser.displayName}"? Past activity will show as unattributed instead of by name.`)) return;
    try { await api.delete(`/users/${targetUser.id}`); onSaved?.(); }
    catch (err) { alert(err.message ?? 'Failed'); }
  };

  const confirmResetPin = async () => {
    if (!newPin.trim()) return;
    setPinBusy(true); setPinMessage(null);
    try {
      await api.post(`/users/${targetUser.id}/reset-pin`, { newPin: newPin.trim() });
      setPinMessage('PIN reset.');
      setNewPin('');
      setResettingPin(false);
    } catch (err) {
      setPinMessage(err.message ?? 'Failed to reset PIN');
    } finally {
      setPinBusy(false);
    }
  };

  return (
    <div class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-10" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div class="w-full max-w-md overflow-hidden rounded-xl border border-border-subtle bg-bg-base">
        <div class="border-b border-border-subtle bg-bg-header px-5 py-4">
          <p class="eyebrow mb-1" style={{ color: ADMIN_BLUE }}>{editing ? 'Edit user' : 'New user'}</p>
        </div>
        <div class="space-y-3 p-5">
          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Username</p>
            <input
              autoFocus={!editing}
              type="text"
              value={form.username ?? ''}
              onInput={set('username')}
              disabled={editing}
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-50"
            />
          </div>
          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Display name</p>
            <input
              autoFocus={editing}
              type="text"
              value={form.displayName ?? ''}
              onInput={set('displayName')}
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>
          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Role</p>
            <select
              value={form.role ?? 'user'}
              onChange={set('role')}
              disabled={isSelf}
              class="block w-full rounded-md border border-border-strong bg-bg-card px-2.5 py-1.5 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-50"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            {isSelf && <p class="mt-1 text-[11px] text-text-muted">You can't change your own role.</p>}
          </div>
          {!editing && (
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">PIN <span class="font-medium text-text-muted">· 4-8 digits</span></p>
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{4,8}"
                value={form.pin ?? ''}
                onInput={set('pin')}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 font-mono text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
          )}

          {editing && !isSelf && (
            <div class="rounded-md border border-dashed border-border-strong bg-bg-card px-3 py-2">
              <p class="m-0 mb-1.5 text-[11px] font-semibold uppercase tracking-eyebrow text-text-muted">Reset PIN</p>
              {!resettingPin ? (
                <button type="button" onClick={() => setResettingPin(true)} class="text-[12px] font-semibold text-brand">
                  Set a new PIN…
                </button>
              ) : (
                <div class="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\d{4,8}"
                    placeholder="New PIN"
                    value={newPin}
                    onInput={(e) => setNewPin(e.currentTarget.value)}
                    class="w-28 rounded-md border border-border-strong bg-bg-base px-2 py-1 font-mono text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
                  />
                  <Button variant="secondary" onClick={confirmResetPin} disabled={pinBusy || !newPin.trim()}>
                    {pinBusy ? 'Resetting…' : 'Confirm reset'}
                  </Button>
                </div>
              )}
              {pinMessage && <p class="mt-1.5 text-[11px] text-text-tertiary">{pinMessage}</p>}
            </div>
          )}

          {error && (
            <p class="text-[13px] font-semibold text-ink-magenta">
              {error instanceof ApiError ? error.message : (error.message ?? 'Failed')}
            </p>
          )}
        </div>
        <div class="flex items-center justify-between gap-2 border-t border-border-subtle bg-bg-header px-5 py-3">
          {editing && !isSelf && (
            <Button variant="ghost" onClick={remove} class="text-ink-magenta hover:text-ink-magenta">Delete</Button>
          )}
          <div class="ml-auto flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button
              variant="primary"
              onClick={save}
              disabled={busy || !form.displayName?.trim() || (!editing && (!form.username?.trim() || !form.pin?.trim()))}
            >
              {busy ? 'Saving…' : editing ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const UsersPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { data: users, refetch } = useApi(isAdmin ? '/users' : null);
  const [editingUser, setEditingUser] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  if (!isAdmin) {
    return <p class="text-sm text-text-tertiary">Admins only.</p>;
  }

  return (
    <div>
      <section class="mb-5 flex items-start justify-between gap-4">
        <div>
          <div class="mb-1.5 flex items-center gap-2">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: ADMIN_BLUE }} />
            <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: ADMIN_BLUE }}>Admin</span>
          </div>
          <h1 class="m-0 mb-1.5 text-[30px] leading-[1.18] tracking-tight text-text-primary">Users</h1>
          <p class="m-0 text-sm text-text-secondary">Who can sign in, and what they can do.</p>
        </div>
        <Button variant="primary" onClick={() => setNewOpen(true)}>+ New user</Button>
      </section>

      {!users && <p class="text-sm text-text-tertiary">Loading…</p>}
      {users && users.length > 0 && (
        <div class="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {users.map((u) => (
            <div
              key={u.id}
              class="relative flex items-center justify-between gap-3 overflow-hidden rounded-md border border-border-subtle bg-bg-card p-3"
            >
              <span class="pointer-events-none absolute inset-y-0 left-0 w-[3px]" style={{ background: ADMIN_BLUE }} aria-hidden="true" />
              <div class="min-w-0 flex-1">
                <div class="flex items-center gap-2">
                  <p class="m-0 truncate font-serif text-[15px] font-medium text-text-primary">{u.displayName}</p>
                  <RolePill role={u.role} />
                </div>
                <p class="m-0 mt-0.5 text-[11px] text-text-tertiary">
                  {u.username} · Last login {formatLastLogin(u.lastLoginAt)}
                </p>
              </div>
              <button onClick={() => setEditingUser(u)} class="text-[11px] text-text-tertiary hover:text-brand" aria-label="Edit">
                Edit
              </button>
            </div>
          ))}
        </div>
      )}

      <UserModal
        open={newOpen}
        currentUserId={user.id}
        onClose={() => setNewOpen(false)}
        onSaved={() => { setNewOpen(false); refetch(); }}
      />
      <UserModal
        open={!!editingUser}
        targetUser={editingUser}
        currentUserId={user.id}
        onClose={() => setEditingUser(null)}
        onSaved={() => { setEditingUser(null); refetch(); }}
      />
    </div>
  );
};
